import {
  ArrowRight,
  Building2,
  ClipboardCheck,
  Compass,
  FileSearch,
  FlaskConical,
  GraduationCap,
  Handshake,
  Lightbulb,
  Route as RouteIcon,
  Scale,
  Sparkles,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { useHealth, useLogout, useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "Ethics Review";

interface PersonaOption {
  id: string;
  label: string;
  icon: typeof FlaskConical;
  defaultPage: string;
  description: string;
  primary?: boolean;
}

// Mirrored locally (not imported from the dashboard) to keep home decoupled from
// the dashboard's persona config. Order puts the recommended role first.
const RESEARCHER: PersonaOption = {
  id: "researcher",
  label: "Onderzoeker",
  icon: FlaskConical,
  defaultPage: "mijn-aanvragen",
  description: "De volledig uitgewerkte flow: dien een nieuwe ethische aanvraag in en volg ze op.",
  primary: true,
};

const PERSONAS: PersonaOption[] = [
  RESEARCHER,
  {
    id: "promoter",
    label: "Promotor",
    icon: GraduationCap,
    defaultPage: "team-aanvragen",
    description:
      "Verken hoe het platform eruitziet voor een promotor die aanvragen van het team opvolgt.",
  },
  {
    id: "reviewer",
    label: "Reviewer",
    icon: ClipboardCheck,
    defaultPage: "bureau",
    description: "Verken hoe het platform eruitziet voor een reviewer tijdens de Bureau review.",
  },
  {
    id: "backoffice",
    label: "Secretariaat",
    icon: Building2,
    defaultPage: "overview",
    description: "Verken hoe het platform eruitziet voor secretariaat en Bureau.",
  },
];

const PRINCIPLES: { title: string; body: string }[] = [
  {
    title: "AI ondersteunt, mensen blijven verantwoordelijk",
    body: "AI mag signaleren, samenvatten, vergelijken en suggesties doen, maar beslist nooit over toewijzing, voorwaarden, ethisch oordeel, ondertekening of communicatie.",
  },
  {
    title: "Bewustwording i.p.v. onbewust accepteren",
    body: "Het proces moet voorkomen dat men AI-output blind accepteert. Onderzoekers, reviewers en Bureau moeten begrijpen wat er speelt, actief wegen en bewust handelen.",
  },
  {
    title: "Transparantie en uitlegbaarheid in elke stap",
    body: "Status, voortgang, AI-signalen, wijzigingen, voorwaarden en reviewacties moeten altijd zichtbaar en begrijpelijk zijn. Iedereen moet direct kunnen zien waar een dossier zich bevindt en waarom een actie of aanvulling nodig is.",
  },
  {
    title: "Begeleiding zonder harde blokkades",
    body: "Het systeem moet bekende fouten voorkomen en feedback direct uitvoerbaar maken, maar mag nooit blokkeren. Daarbij wordt iedere actie van AI bijgehouden in een log om transparantie te behouden.",
  },
  {
    title: "Werkdruk verlagen, niet verplaatsen",
    body: "De review blijft nodig, maar wachttijd, herhaalvragen en reconstructie-werk verminderen voor onderzoekers, secretariaat, reviewers en Bureau en voorkomt het creëren van nieuwe bottlenecks.",
  },
];

const PROCESS_FLOW: {
  label: string;
  role: string;
  title: string;
  body: string;
  icon: typeof FlaskConical;
}[] = [
  {
    label: "01",
    role: "Onderzoeker",
    title: "Geleide intake & triage",
    body: "De onderzoeker start met begeleiding, een checklist, tip-sheet hulp en zicht op de tijdlijn.",
    icon: FlaskConical,
  },
  {
    label: "02",
    role: "AI pre-screening",
    title: "Volledigheidscheck",
    body: "AI markeert ontbrekende stukken, veelvoorkomende fouten en bekende risico's, zonder ooit te blokkeren.",
    icon: FileSearch,
  },
  {
    label: "03",
    role: "Secretariaat",
    title: "Toewijzing reviewer & AI-briefing",
    body: "Het secretariaat controleert volledigheid, bewaakt de status en wijst een reviewer toe; AI levert een samenvatting met bronverwijzingen.",
    icon: Building2,
  },
  {
    label: "04",
    role: "Reviewer",
    title: "Menselijke review",
    body: "De reviewer weegt AI-signalen bewust, valideert, verwerpt of vult aan en formuleert feedback of voorwaarden. Het menselijk oordeel blijft leidend.",
    icon: ClipboardCheck,
  },
  {
    label: "05",
    role: "Bureau",
    title: "Beslissing & ondertekend advies",
    body: "Het Bureau bespreekt en beslist, houdt de audit trail zichtbaar en de voorzitter ondertekent het advies. Communicatie blijft menselijk en herleidbaar.",
    icon: Scale,
  },
  {
    label: "06",
    role: "Revisieronde",
    title: "Slimme revisieronde",
    body: "Bij een hersubmissie vergelijkt AI de aanpassingen met de gestelde voorwaarden, ter ondersteuning van de menselijke check.",
    icon: Handshake,
  },
];

const FINDINGS: string[] = [
  "Onderzoekers, secretariaat, reviewers en Bureau verwerken samen zo'n 250 unieke dossiers per jaar. Door hersubmissies loopt het totaal aantal indieningen op tot 600 à 700: ongeveer 75% van de dossiers komt meer dan één keer terug.",
  "Problemen met informed consent zijn de belangrijkste oorzaak van hersubmissies.",
  "Het uitgangspunt van dit ontwerp: AI helpt bij voorbereiding, samenvatting en snelheid, maar mensen blijven verantwoordelijk voor elk oordeel, elke voorwaarde en elke ondertekening.",
];

export function HomePage() {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: health } = useHealth();
  const { data: me } = useMe();

  const [selectedPersona, setSelectedPersona] = useState<string>("researcher");

  const backendStatus = health?.status ?? "unreachable";

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  const active = PERSONAS.find((p) => p.id === selectedPersona) ?? RESEARCHER;

  const handleOpenPersona = () => {
    try {
      localStorage.setItem("ea-shw-persona", active.id);
    } catch {
      // ignore
    }
    void navigate(`/dashboard/${active.defaultPage}`);
  };

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border/80 bg-card/95 sticky top-0 z-10 flex flex-col gap-4 border-b px-5 py-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-center gap-4">
          <img src="/brand/logo.png" alt="Universiteit Antwerpen" className="h-10 w-auto" />
          <div className="min-w-0">
            <p className="text-support text-sm font-semibold">{PROJECT_NAME}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Link to="/profile">
            <Button variant="ghost" size="sm">
              Profiel
            </Button>
          </Link>
          {me?.role === "admin" && (
            <>
              <Link to="/admin/users">
                <Button variant="ghost" size="sm">
                  Admin
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="ghost" size="sm">
                  Activiteit
                </Button>
              </Link>
            </>
          )}
          <Button variant="outline" onClick={handleLogout}>
            Afmelden
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-[1040px] space-y-14 px-5 py-12 sm:px-6 sm:py-14">
        {/* 1. Hero */}
        <section className="mx-auto max-w-3xl text-center">
          <span className="bg-support-light/60 text-support inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            Prototype v0
          </span>
          <h1 className="font-heading text-support mt-5 text-4xl font-bold tracking-tight sm:text-5xl">
            Ethische toetsing, slimmer voorbereid
          </h1>
          <p className="text-muted mx-auto mt-5 max-w-2xl text-lg">
            Dit prototype verkent hoe AI-ondersteuning het proces van ethische aanvragen bij de
            Ethische Adviescommissie Sociale en Humane Wetenschappen (EA SHW) kan versnellen, zonder
            dat AI ooit zelf beslist.
          </p>
        </section>

        {/* 2. Prototype disclaimer */}
        <section className="border-border bg-card border-l-support flex flex-col gap-4 rounded-lg border border-l-4 p-6 sm:flex-row sm:p-7">
          <div className="bg-support-light/70 text-support flex h-9 w-9 shrink-0 items-center justify-center rounded-md">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <p className="font-heading text-support text-2xl leading-tight font-semibold">
              Wat je hier ziet is een vroeg prototype
            </p>
            <div className="text-muted mt-4 grid gap-3 text-sm leading-relaxed md:grid-cols-3">
              <p>
                Dit is een vroeg prototype (v0), geen afgewerkt product. Het is bedoeld om richting
                en ideeën te toetsen, niet om al het detailwerk te tonen.
              </p>
              <p>
                De volledig uitgewerkte flow is{" "}
                <strong className="text-foreground font-semibold">
                  de nieuwe aanvraag voor onderzoekers
                </strong>
                . Dat is waar je het meeste detail en realisme zult vinden.
              </p>
              <p>
                De andere rollen (Promotor, Reviewer en Secretariaat) zijn vooral bedoeld om een
                gevoel te geven van hoe het platform er voor hen zou uitzien. Ze zijn bewust nog
                minder volledig uitgewerkt.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Research findings */}
        <section>
          <SectionHeading icon={Lightbulb} title="Achtergrond over het project" />
          <div className="mt-5 grid auto-rows-fr gap-4 md:grid-cols-3">
            {FINDINGS.map((finding, index) => (
              <div
                key={index}
                className="border-border bg-card flex rounded-lg border p-5 text-sm leading-relaxed"
              >
                <p className="text-foreground/80">{finding}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 4. Design principles */}
        <section>
          <SectionHeading icon={Compass} title="Ontwerpprincipes" />
          <div className="mt-5 grid auto-rows-fr gap-4 md:grid-cols-2 lg:grid-cols-6">
            {PRINCIPLES.map((principle, index) => (
              <PrincipleCard
                key={principle.title}
                number={index + 1}
                title={principle.title}
                body={principle.body}
                className={index < 2 ? "lg:col-span-3" : "lg:col-span-2"}
              />
            ))}
          </div>
        </section>

        {/* 5. To-be process */}
        <section>
          <SectionHeading icon={RouteIcon} title="Het beoogde proces" />
          <p className="text-muted mt-2 max-w-2xl text-sm">
            Van intake tot ondertekend advies: AI ondersteunt elke stap, maar het menselijk oordeel
            blijft leidend.
          </p>

          <ProcessDiagram />
        </section>

        {/* 6. Persona launcher */}
        <section>
          <SectionHeading icon={Users} title="Kies je rol" />
          <p className="text-muted mt-2 max-w-2xl text-sm">
            Bekijk het prototype vanuit een specifieke rol. Wil je de meest complete ervaring? Kies
            dan Onderzoeker.
          </p>

          <div className="mt-5 grid auto-rows-fr gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PERSONAS.map((persona) => (
              <PersonaTile
                key={persona.id}
                persona={persona}
                active={persona.id === selectedPersona}
                onSelect={() => setSelectedPersona(persona.id)}
              />
            ))}
          </div>

          <div className="mt-6">
            <Button size="lg" onClick={handleOpenPersona} className="gap-2">
              Open {active.label} weergave
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </section>
      </main>

      {/* 7. Footer */}
      <footer className="border-border/80 mt-8 border-t px-5 py-4 sm:px-6">
        <p className="text-muted text-xs">
          Backend status:{" "}
          <span className={backendStatus === "ok" ? "text-green-600" : "text-red-500"}>
            {backendStatus}
          </span>
        </p>
      </footer>
    </div>
  );
}

interface SectionHeadingProps {
  icon: typeof FlaskConical;
  title: string;
}

function SectionHeading({ icon: Icon, title }: SectionHeadingProps) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="text-support h-5 w-5" aria-hidden="true" />
      <p className="font-heading text-support text-2xl leading-tight font-bold">{title}</p>
    </div>
  );
}

interface PrincipleCardProps {
  number: number;
  title: string;
  body: string;
  className?: string;
}

function PrincipleCard({ number, title, body, className }: PrincipleCardProps) {
  return (
    <div className={cn("border-border bg-card flex rounded-lg border p-5", className)}>
      <div className="flex flex-col">
        <div className="flex items-start gap-3">
          <span className="bg-support-light/80 text-support flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-xs font-bold">
            {number}
          </span>
          <p className="font-heading text-support text-lg leading-snug font-semibold">{title}</p>
        </div>
        <p className="text-muted mt-3 text-sm leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function ProcessDiagram() {
  return (
    <figure className="border-border bg-card mt-5 rounded-lg border p-5 sm:p-6">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {PROCESS_FLOW.map((step) => {
          const Icon = step.icon;

          return (
            <div key={step.title} className="relative">
              <div className="bg-background flex h-full rounded-md p-3 ring-1 ring-black/5 sm:p-4">
                <div className="flex w-full min-w-0 flex-col">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted text-xs font-semibold">{step.label}</span>
                    <span className="bg-support-light text-support flex h-8 w-8 items-center justify-center rounded-md">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  </div>
                  <p className="text-muted mt-4 text-[11px] font-semibold tracking-wide uppercase">
                    {step.role}
                  </p>
                  <p className="font-heading text-support mt-1 text-lg leading-tight font-semibold break-words">
                    {step.title}
                  </p>
                  <p className="text-muted mt-2 text-xs leading-relaxed">{step.body}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </figure>
  );
}

interface PersonaTileProps {
  persona: PersonaOption;
  active: boolean;
  onSelect: () => void;
}

function PersonaTile({ persona, active, onSelect }: PersonaTileProps) {
  const Icon = persona.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        "relative flex h-full min-h-48 flex-col rounded-lg border p-5 text-left transition-all",
        active
          ? "border-support bg-support-light/40 ring-support/20 ring-2"
          : "border-border bg-card hover:border-support/50 hover:-translate-y-0.5 hover:shadow-md",
      )}
    >
      {persona.primary && (
        <span className="bg-primary text-primary-foreground absolute top-4 right-4 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
          Aanbevolen
        </span>
      )}
      <span
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-full transition-colors",
          active ? "bg-support text-support-foreground" : "bg-support-light text-support",
        )}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5" />
      </span>
      <p className="font-heading text-support mt-3.5 text-xl leading-tight font-semibold">
        {persona.label}
      </p>
      <p className="text-muted mt-1.5 text-sm leading-relaxed">{persona.description}</p>
    </button>
  );
}
